import { useState, type FormEvent } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  ROTULO_FORMA_PAGAMENTO,
  TIPO_DE_MOVIMENTO_DE_CAIXA,
  type CaixaDetalhado,
  type CaixaNaLista,
  type ForaDoCaixa,
  type LancamentoDoCaixa,
  type TipoDeMovimentoDeCaixa,
} from '@arena/shared/schemas';
import { dataValida } from '@arena/shared/tempo';
import { dataCurta, dinheiro, paraCentavos } from '../agenda/formatos.js';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { caixaApi } from '../api/caixa.js';
import { estoqueApi } from '../api/estoque.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import {
  Botao,
  Campo,
  Card,
  Escolhas,
  Feed,
  Formulario,
  ItemDeFeed,
  Kpi,
  Mini,
  Nota,
  Status,
  Tabela,
  Vazio,
} from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import {
  corDoLancamento,
  corpoDoFechamentoDeCaixa,
  diferencaDoCaixa,
  erroDoMovimentoDeCaixa,
  horaDoInstante,
  valorDoLancamento,
} from './formatos.js';

/**
 * O caixa da recepção — a sessão que abre com o troco, acumula o dinheiro
 * em espécie que o razão recebe e fecha com o que a pessoa CONTOU na gaveta.
 *
 * A tela só mostra e envia: o esperado é do servidor, que é quem sabe o que
 * entrou. A diferença "ao vivo" do fechamento é a mesma conta, feita aqui
 * para a pessoa enxergá-la antes de confirmar; o `esperadoConferido` que vai
 * junto faz o servidor recusar (409) se entrou dinheiro no meio. A
 * permissão aqui é COSMÉTICA — esconde botão; quem decide é o servidor.
 *
 * O dia do "dinheiro fora do caixa" mora na URL (`?dia=2026-09-14`).
 *
 * Ver: Arena_backend/Docs/plano/21-caixa-da-recepcao.md
 */
export function PaginaDoCaixa() {
  const { pode } = useSessao();
  const atual = useQuery({
    queryKey: ['caixa', 'atual'],
    queryFn: caixaApi.atual,
    staleTime: 30_000,
  });
  const podeOperar = pode('financeiro', 'criar');
  const caixa = atual.data?.caixa ?? null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Caixa da recepção</h1>
          <p>
            {caixa
              ? `Aberto desde ${horaDoInstante(caixa.abertoEm)} de ${dataCurta(caixa.dataAbertura)}${
                  caixa.abertoPor ? ` por ${caixa.abertoPor}` : ''
                }.`
              : 'Abra o caixa com o troco da gaveta; feche contando o dinheiro.'}
          </p>
        </div>
        {caixa && <Status situacao="ok">Aberto</Status>}
      </div>

      <Nota>
        <b>Só o dinheiro em espécie é conferido.</b> Pix, cartão e boleto aparecem como informativo:
        não há gaveta para contar. Sangria e suprimento mexem no esperado, mas não são receita nem
        despesa.
      </Nota>

      {atual.isPending ? (
        <EsqueletoDeCard linhas={4} />
      ) : atual.isError ? (
        <Erro detalhe={mensagemDoErro(atual.error)} onTentarDeNovo={() => void atual.refetch()} />
      ) : caixa ? (
        <CaixaAberto caixa={caixa} podeOperar={podeOperar} />
      ) : (
        <AberturaDoCaixa podeOperar={podeOperar} />
      )}

      <div className="grid g-2-1" style={{ marginTop: 16 }}>
        <HistoricoDeCaixas />
        <ForaDoCaixaDoDia />
      </div>
    </>
  );
}

/** O que muda depois de abrir, fechar ou movimentar: o caixa, o painel do Bar (o chip) e o razão. */
function useAtualizarCaixa() {
  const cliente = useQueryClient();
  return (comRazao: boolean) =>
    Promise.all([
      cliente.invalidateQueries({ queryKey: ['caixa'] }),
      cliente.invalidateQueries({ queryKey: ['comandas', 'painel'] }),
      ...(comRazao ? [cliente.invalidateQueries({ queryKey: ['financeiro'] })] : []),
    ]);
}

function Aviso({ children }: { children: string }) {
  return (
    <div className="aviso conflito" role="alert">
      <Icone nome="info" />
      <div>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Sem caixa aberto
// ---------------------------------------------------------------------

function AberturaDoCaixa({ podeOperar }: { podeOperar: boolean }) {
  const avisar = useToast();
  const atualizar = useAtualizarCaixa();
  const [troco, setTroco] = useState('');
  const [erro, setErro] = useState<string | undefined>();

  const abrir = useMutation({
    mutationFn: (trocoInicial: number) => caixaApi.abrir({ trocoInicial }),
    onSuccess: async () => {
      await atualizar(false);
      avisar('Caixa aberto.');
    },
    onError: (e: unknown) => setErro(mensagemDoErro(e)),
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const centavos = paraCentavos(troco);
    if (centavos === null) {
      setErro('Informe o troco da gaveta (zero, se não há).');
      return;
    }
    setErro(undefined);
    abrir.mutate(centavos);
  }

  if (!podeOperar) {
    return (
      <Card titulo="Nenhum caixa aberto">
        <Vazio>Não há caixa aberto agora. Quem abre é a recepção.</Vazio>
      </Card>
    );
  }

  return (
    <Card titulo="Abrir caixa" dica="o troco que já está na gaveta">
      <Formulario onSubmit={enviar}>
        <Campo rotulo="Troco inicial">
          <input
            inputMode="decimal"
            placeholder="R$ 100,00"
            value={troco}
            onChange={(e) => setTroco(e.target.value)}
          />
        </Campo>
        {erro && <Aviso>{erro}</Aviso>}
        <div>
          <Botao type="submit" disabled={abrir.isPending}>
            {abrir.isPending ? 'Abrindo…' : 'Abrir caixa'}
          </Botao>
        </div>
      </Formulario>
    </Card>
  );
}

// ---------------------------------------------------------------------
// Com caixa aberto
// ---------------------------------------------------------------------

function CaixaAberto({ caixa, podeOperar }: { caixa: CaixaDetalhado; podeOperar: boolean }) {
  const [fechando, setFechando] = useState(false);
  const din = caixa.dinheiro;

  return (
    <>
      <div className="grid g4">
        <Kpi valor={dinheiro(caixa.trocoInicial)} rotulo="Troco inicial" icone="money" />
        <Kpi valor={dinheiro(din.entradas - din.saidas)} rotulo="Dinheiro recebido" icone="tag" />
        <Kpi
          valor={dinheiro(din.suprimentos - din.sangrias)}
          rotulo="Sangrias e suprimentos"
          icone="box"
        />
        <Kpi valor={dinheiro(caixa.esperado)} rotulo="Esperado na gaveta" icone="chart" />
      </div>

      <div className="grid g-2-1" style={{ marginTop: 16 }}>
        <Card
          titulo="Lançamentos do caixa"
          dica={`${din.quantidade} ${din.quantidade === 1 ? 'lançamento' : 'lançamentos'} em dinheiro`}
        >
          {caixa.lancamentos.length === 0 ? (
            <Vazio>Nenhum dinheiro entrou ainda.</Vazio>
          ) : (
            <ListaDeLancamentos lancamentos={caixa.lancamentos} />
          )}
          {din.quantidade > caixa.lancamentos.length && (
            <p className="hint" style={{ marginTop: 10 }}>
              Mostrando os {caixa.lancamentos.length} mais recentes; o esperado soma todos.
            </p>
          )}
        </Card>

        <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
          <Card
            titulo="Pix, cartão e boleto"
            dica={`${dataCurta(caixa.janela.de)} a ${dataCurta(caixa.janela.ate)} · informativo`}
          >
            <Mini
              itens={caixa.outrasFormas.map((f) => ({
                valor: dinheiro(f.total),
                rotulo: ROTULO_FORMA_PAGAMENTO[f.forma],
              }))}
            />
            <p className="hint" style={{ marginTop: 10 }}>
              Não entram no esperado: não há o que contar na gaveta.
            </p>
          </Card>

          <MensalidadeDoPeriodo de={caixa.janela.de} ate={caixa.janela.ate} />
        </div>
      </div>

      <div className="grid g-2-1" style={{ marginTop: 16 }}>
        <Card titulo="Sangrias e suprimentos" dica="o motivo fica registrado">
          {caixa.movimentos.length === 0 ? (
            <Vazio>Nenhum movimento neste caixa.</Vazio>
          ) : (
            <Feed>
              {caixa.movimentos.map((m) => (
                <ItemDeFeed
                  key={m.id}
                  titulo={`${m.tipo === 'Sangria' ? 'Sangria' : 'Suprimento'} · ${m.motivo}`}
                  subtitulo={`${horaDoInstante(m.criadoEm)}${m.criadoPor ? ` · ${m.criadoPor}` : ''}`}
                  direita={
                    <b className="num">
                      {m.tipo === 'Sangria' ? '−' : '+'} {dinheiro(m.valor)}
                    </b>
                  }
                />
              ))}
            </Feed>
          )}
          {podeOperar && <FormularioDeMovimento caixaId={caixa.id} />}
        </Card>

        {podeOperar && (
          <Card titulo="Fechar caixa" dica="conte a gaveta">
            {fechando ? (
              <FormularioDeFechamento caixa={caixa} onCancelar={() => setFechando(false)} />
            ) : (
              <>
                <p className="hint" style={{ marginBottom: 12 }}>
                  Esperado agora: <b className="num">{dinheiro(caixa.esperado)}</b>.
                </p>
                <Botao onClick={() => setFechando(true)}>Fechar caixa</Botao>
              </>
            )}
          </Card>
        )}
      </div>
    </>
  );
}

function ListaDeLancamentos({ lancamentos }: { lancamentos: readonly LancamentoDoCaixa[] }) {
  return (
    <Feed>
      {lancamentos.map((l) => (
        <ItemDeFeed
          key={l.id}
          titulo={l.descricao}
          subtitulo={`${dataCurta(l.data)} · ${horaDoInstante(l.criadoEm)}${l.estorno ? ' · estorno' : ''}`}
          direita={
            <b className="num" style={{ color: corDoLancamento(l) }}>
              {valorDoLancamento(l)}
            </b>
          }
        />
      ))}
    </Feed>
  );
}

function FormularioDeMovimento({ caixaId }: { caixaId: string }) {
  const avisar = useToast();
  const atualizar = useAtualizarCaixa();
  const [tipo, setTipo] = useState<TipoDeMovimentoDeCaixa>('Sangria');
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | undefined>();

  const movimentar = useMutation({
    mutationFn: (centavos: number) =>
      caixaApi.movimentar(caixaId, { tipo, valor: centavos, motivo: motivo.trim() }),
    onSuccess: async () => {
      await atualizar(false);
      avisar(tipo === 'Sangria' ? 'Sangria registrada.' : 'Suprimento registrado.');
      setValor('');
      setMotivo('');
    },
    onError: (e: unknown) => setErro(mensagemDoErro(e)),
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const centavos = valor.trim() ? paraCentavos(valor) : null;
    const problema = erroDoMovimentoDeCaixa(centavos, motivo);
    if (problema || centavos === null) {
      setErro(problema);
      return;
    }
    setErro(undefined);
    movimentar.mutate(centavos);
  }

  return (
    <Formulario onSubmit={enviar} colunas={2} style={{ marginTop: 14 }}>
      <Campo rotulo="Movimento">
        <Escolhas
          aria-label="Tipo de movimento"
          valor={tipo}
          onChange={setTipo}
          opcoes={TIPO_DE_MOVIMENTO_DE_CAIXA.map((t) => ({
            valor: t,
            rotulo: t === 'Sangria' ? 'Sangria (sai)' : 'Suprimento (entra)',
          }))}
        />
      </Campo>
      <Campo rotulo="Valor">
        <input
          inputMode="decimal"
          placeholder="R$ 50,00"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
        />
      </Campo>
      <Campo rotulo="Motivo" ajuda="Ex.: levado ao cofre; troco trazido do banco.">
        <input value={motivo} maxLength={200} onChange={(e) => setMotivo(e.target.value)} />
      </Campo>
      <div style={{ alignSelf: 'end' }}>
        <Botao type="submit" variante="contorno" disabled={movimentar.isPending}>
          {movimentar.isPending ? 'Registrando…' : 'Registrar'}
        </Botao>
      </div>
      {erro && <Aviso>{erro}</Aviso>}
    </Formulario>
  );
}

const ROTULO_DA_DIFERENCA = {
  falta: 'Falta',
  sobra: 'Sobra',
  confere: 'Confere',
} as const;
const SITUACAO_DA_DIFERENCA = {
  falta: 'atrasada',
  sobra: 'atencao',
  confere: 'ok',
} as const;

function FormularioDeFechamento({
  caixa,
  onCancelar,
}: {
  caixa: CaixaDetalhado;
  onCancelar: () => void;
}) {
  const avisar = useToast();
  const cliente = useQueryClient();
  const atualizar = useAtualizarCaixa();
  const [contadoTexto, setContadoTexto] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState<string | undefined>();

  const contado = contadoTexto.trim() ? paraCentavos(contadoTexto) : null;
  const diferenca = contado === null ? null : diferencaDoCaixa(caixa.esperado, contado);

  const fechar = useMutation({
    mutationFn: (corpo: Parameters<typeof caixaApi.fechar>[1]) => caixaApi.fechar(caixa.id, corpo),
    onSuccess: async (resultado) => {
      await atualizar(true);
      avisar(
        resultado.lancamentoDaDiferencaId
          ? 'Caixa fechado. A diferença foi lançada no financeiro.'
          : 'Caixa fechado. Conferiu.',
      );
    },
    onError: async (e: unknown) => {
      setErro(mensagemDoErro(e));
      // 409: o esperado mudou (entrou dinheiro no meio) ou o caixa já fechou.
      // Recarrega para a pessoa conferir o número novo antes de tentar de novo.
      if (e instanceof ErroDeApi && e.status === 409) {
        await cliente.invalidateQueries({ queryKey: ['caixa'] });
      }
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const pedido = corpoDoFechamentoDeCaixa({ esperado: caixa.esperado, contado, justificativa });
    if ('erro' in pedido) {
      setErro(pedido.erro);
      return;
    }
    setErro(undefined);
    fechar.mutate(pedido.corpo);
  }

  return (
    <Formulario onSubmit={enviar}>
      <Mini
        itens={[
          { valor: dinheiro(caixa.esperado), rotulo: 'Esperado' },
          { valor: contado === null ? '—' : dinheiro(contado), rotulo: 'Contado' },
        ]}
      />
      <Campo rotulo="Contado na gaveta">
        <input
          inputMode="decimal"
          placeholder="R$ 0,00"
          value={contadoTexto}
          autoFocus
          onChange={(e) => setContadoTexto(e.target.value)}
        />
      </Campo>

      {diferenca && (
        <div aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Status situacao={SITUACAO_DA_DIFERENCA[diferenca.tipo]}>
            {ROTULO_DA_DIFERENCA[diferenca.tipo]}
          </Status>
          {diferenca.tipo === 'confere' ? (
            <span className="hint">A gaveta bate com o esperado.</span>
          ) : (
            <b className="num">{dinheiro(diferenca.valor)}</b>
          )}
        </div>
      )}

      {diferenca && diferenca.tipo !== 'confere' && (
        <Campo
          rotulo="Justificativa"
          ajuda={`Vira ${diferenca.tipo === 'falta' ? 'Quebra' : 'Sobra'} de caixa no financeiro.`}
        >
          <textarea
            rows={3}
            maxLength={500}
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
          />
        </Campo>
      )}

      {erro && <Aviso>{erro}</Aviso>}
      <div style={{ display: 'flex', gap: 8 }}>
        <Botao type="submit" disabled={fechar.isPending}>
          {fechar.isPending ? 'Fechando…' : 'Confirmar fechamento'}
        </Botao>
        <Botao variante="fantasma" onClick={onCancelar} disabled={fechar.isPending}>
          Voltar
        </Botao>
      </div>
    </Formulario>
  );
}

/**
 * A mensalidade do período: o consumo de bar que foi para a fatura do aluno
 * durante o caixa. Não é dinheiro na gaveta — fica ao lado para a recepção
 * explicar a diferença entre o que o bar vendeu e o que o caixa recebeu. O
 * financeiro não lê comandas, então o front junta as duas chamadas: aqui, o
 * histórico de comandas fechadas na forma Mensalidade.
 */
function MensalidadeDoPeriodo({ de, ate }: { de: string; ate: string }) {
  const { pode } = useSessao();
  const permitido = pode('comanda', 'ler');
  const consulta = useQuery({
    queryKey: ['caixa', 'mensalidade', de, ate],
    queryFn: () =>
      estoqueApi.historicoDeComandas({
        situacao: 'fechadas',
        forma: 'Mensalidade',
        de,
        ate,
        limite: 1,
      }),
    enabled: permitido,
  });

  if (!permitido) return null;

  return (
    <Card titulo="Mensalidade do período" dica="consumo lançado na fatura do aluno">
      {consulta.isPending ? (
        <EsqueletoDeCard linhas={1} />
      ) : consulta.isError ? (
        <Erro
          detalhe={mensagemDoErro(consulta.error)}
          onTentarDeNovo={() => void consulta.refetch()}
        />
      ) : (
        <>
          <b className="num" style={{ fontSize: 20 }}>
            {dinheiro(consulta.data.totaisPorForma.Mensalidade)}
          </b>
          <p className="hint" style={{ marginTop: 6 }}>
            Comandas fechadas de {dataCurta(de)} a {dataCurta(ate)}. Não entra no esperado.
          </p>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------
// Histórico e dinheiro fora do caixa
// ---------------------------------------------------------------------

function HistoricoDeCaixas() {
  const historico = useInfiniteQuery({
    queryKey: ['caixa', 'historico'],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      caixaApi.historico({ cursor: pageParam, limite: 25 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
  });
  const itens = historico.data?.pages.flatMap((p) => p.itens) ?? [];

  return (
    <Card titulo="Histórico de caixas">
      {historico.isPending ? (
        <EsqueletoDeCard linhas={3} />
      ) : historico.isError ? (
        <Erro
          detalhe={mensagemDoErro(historico.error)}
          onTentarDeNovo={() => void historico.refetch()}
        />
      ) : itens.length === 0 ? (
        <Vazio>Nenhum caixa aberto até agora.</Vazio>
      ) : (
        <>
          <Tabela colunas={['Dia', 'Aberto por', 'Esperado', 'Contado', 'Diferença']}>
            {itens.map((c) => (
              <LinhaDoHistorico key={c.id} caixa={c} />
            ))}
          </Tabela>
          {historico.hasNextPage && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 2px' }}>
              <Botao
                variante="contorno"
                pequeno
                onClick={() => void historico.fetchNextPage()}
                disabled={historico.isFetchingNextPage}
              >
                {historico.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </Botao>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function LinhaDoHistorico({ caixa }: { caixa: CaixaNaLista }) {
  const diferenca =
    caixa.esperado !== null && caixa.contado !== null
      ? diferencaDoCaixa(caixa.esperado, caixa.contado)
      : null;

  return (
    <tr>
      <td>
        {dataCurta(caixa.dataAbertura)}
        <small style={{ display: 'block', color: 'var(--dim)' }}>
          {horaDoInstante(caixa.abertoEm)}
          {caixa.fechadoEm ? ` – ${horaDoInstante(caixa.fechadoEm)}` : ''}
        </small>
      </td>
      <td>{caixa.abertoPor ?? '—'}</td>
      <td className="num">{caixa.esperado === null ? '—' : dinheiro(caixa.esperado)}</td>
      <td className="num">{caixa.contado === null ? '—' : dinheiro(caixa.contado)}</td>
      <td title={caixa.justificativa ?? undefined}>
        {diferenca === null ? (
          <Status situacao="aguardando">Aberto</Status>
        ) : (
          <Status situacao={SITUACAO_DA_DIFERENCA[diferenca.tipo]}>
            {diferenca.tipo === 'confere'
              ? 'Confere'
              : `${ROTULO_DA_DIFERENCA[diferenca.tipo]} ${dinheiro(diferenca.valor)}`}
          </Status>
        )}
      </td>
    </tr>
  );
}

function ForaDoCaixaDoDia() {
  const [params, setParams] = useSearchParams();
  const pedido = params.get('dia');
  const dia = pedido && dataValida(pedido) ? pedido : undefined;

  const fora = useQuery({
    queryKey: ['caixa', 'fora', dia ?? 'hoje'],
    queryFn: () => caixaApi.fora({ dia }),
  });

  function trocarDia(novo: string) {
    const novos = new URLSearchParams(params);
    if (novo && dataValida(novo)) novos.set('dia', novo);
    else novos.delete('dia');
    setParams(novos, { replace: true });
  }

  return (
    <Card
      titulo={dia ? 'Dinheiro fora do caixa' : 'Dinheiro fora do caixa hoje'}
      dica="recebido sem caixa aberto"
    >
      <Campo rotulo="Dia">
        <input
          type="date"
          value={dia ?? fora.data?.dia ?? ''}
          onChange={(e) => trocarDia(e.target.value)}
        />
      </Campo>
      {fora.isPending ? (
        <EsqueletoDeCard linhas={2} />
      ) : fora.isError ? (
        <Erro detalhe={mensagemDoErro(fora.error)} onTentarDeNovo={() => void fora.refetch()} />
      ) : (
        <ConteudoForaDoCaixa fora={fora.data} />
      )}
    </Card>
  );
}

function ConteudoForaDoCaixa({ fora }: { fora: ForaDoCaixa }) {
  if (fora.quantidade === 0) {
    return <Vazio>Nenhum dinheiro fora do caixa em {dataCurta(fora.dia)}.</Vazio>;
  }
  return (
    <>
      <p style={{ margin: '12px 0' }}>
        <b className="num" style={{ fontSize: 20 }}>
          {dinheiro(fora.total)}
        </b>{' '}
        <span className="hint">
          em {fora.quantidade} {fora.quantidade === 1 ? 'lançamento' : 'lançamentos'}
        </span>
      </p>
      <ListaDeLancamentos lancamentos={fora.lancamentos} />
      {fora.quantidade > fora.lancamentos.length && (
        <p className="hint" style={{ marginTop: 10 }}>
          Mostrando os {fora.lancamentos.length} mais recentes; o total soma todos.
        </p>
      )}
    </>
  );
}
